import React, { FC, Fragment, useEffect } from "react";
import { Form } from "@ant-design/compatible";
import "@ant-design/compatible/assets/index.css";
import { WrappedFormUtils } from "@ant-design/compatible/lib/form/Form";
import moment from "moment";
import {
	CustomField,
	CustomFieldType
} from "@/Contracts/Settings";
import { capitalizeFirstLetter } from "@/Contracts/Utils";
import {
	MergeTagVariable,
	MergeTagVariableDataType
} from "@/Contracts/Documents";
import { FormAlignment, CurrencyOptions } from "@/Contracts";

import ZEditorMergeTag from "../ZMergeTag/ZEditorMergetag";
import ZDatePicker from "../ZDatePicker";
import { ZSelect, ZTooltip, ZTypography } from "@atoms";
import ZInput from "../ZInput";
import ZInputNumber from "@atoms/ZInputNumber";

const { ZText } = ZTypography;
export interface CustomFieldInitialValue {
	id: string;
	value?: string | number | Date;
}
interface Props {
	readonly customField: CustomField;
	readonly isReadonly?: boolean;
	readonly form: WrappedFormUtils<any>;
	readonly values?: string[];
	readonly formAlignment?: FormAlignment;
	readonly customFieldList?: CustomField[];
	readonly initialValues?: CustomFieldInitialValue[];
	readonly mergeTagUsed?: boolean;
	readonly shouldShowTooltip?: boolean;
	readonly mergeTags?: MergeTagVariable[];
	readonly from?: string;
}

const RenderCustomFieldFormItem: FC<Props> = props => {
	const { customField, form, values } = props;

	const isReadonly =
		props.isReadonly || customField.is_value_editable_from_ui === false;

	const getMergeTagDataType = (field: CustomFieldType) => {
		switch (field) {
			case CustomFieldType.TextField:
				return MergeTagVariableDataType.TEXT;
			case CustomFieldType.NumberField:
				return MergeTagVariableDataType.NUMBER;
			case CustomFieldType.DecimalField:
				return MergeTagVariableDataType.DOUBLE;
			case CustomFieldType.AmountField:
				return MergeTagVariableDataType.DOUBLE;
			default:
				return undefined;
		}
	};

	const data_type_merge_tag = getMergeTagDataType(customField.field_type);

	const parentValueChanged = (_value: string) => {
		const childCustomField = props.customFieldList?.find(
			field => field.parent_field_info?.parent_id === customField.id
		);

		if (childCustomField) {
			const formFieldName = `custom_field_form_values.${childCustomField.id}`;
			form.setFieldsValue({
				[formFieldName]: undefined
			});
		}
	};
	const getCustomFieldFormItem = (currentCustomField: CustomField) => {
		switch (currentCustomField.field_type) {
			case CustomFieldType.TextField: {
				return (
					<ZInput
						data-testid="text-custom-field"
						disabled={isReadonly}
						placeholder={`Please enter ${currentCustomField.display_name}`}
						className="w-full"
					/>
				);
			}
			case CustomFieldType.UrlField: {
				return (
					<ZInput
						data-testid="text-custom-field"
						disabled={isReadonly}
						placeholder={`Please enter ${currentCustomField.display_name}`}
						className="w-full"
					/>
				);
			}
			case CustomFieldType.DateField: {
				return (
					<ZDatePicker
						data-testid="date-custom-field"
						disabled={isReadonly}
						placeholder={`Please enter ${currentCustomField.display_name}`}
						className="date-picker full-width"
					/>
				);
			}
			case CustomFieldType.NumberField: {
				const inputOnChange = (
					event: React.KeyboardEvent<HTMLInputElement>
				) => {
					if (event.key === "." || event.key === "e") {
						event.preventDefault();
					}
				};
				return (
					<ZInputNumber
						data-testid="number-custom-field"
						disabled={isReadonly}
						min={0}
						step={1}
						type="number"
						placeholder={`Please enter ${currentCustomField.display_name}`}
						className="input-number-width-full"
						onKeyDown={inputOnChange}
					/>
				);
			}
			case CustomFieldType.DecimalField:
			case CustomFieldType.AmountField: {
				const inputOnChange = (
					event: React.KeyboardEvent<HTMLInputElement>
				) => {
					if (event.key === "e") {
						event.preventDefault();
					}
				};
				return (
					<ZInputNumber
						data-testid="decimal-custom-field"
						disabled={isReadonly}
						min={0}
						step={0.01}
						type="number"
						placeholder={`Please enter ${currentCustomField.display_name}`}
						className="input-number-width-full"
						onKeyDown={inputOnChange}
					/>
				);
			}
			case CustomFieldType.Dropdown:
			case CustomFieldType.MultiSelect: {
				const parentCustomFieldId =
					currentCustomField.parent_field_info?.parent_id;

				if (!!parentCustomFieldId) {
					const parentCustomField = props.customFieldList?.find(
						field => field.id === parentCustomFieldId
					);

					if (!parentCustomField) {
						throw new Error(
							`No parent custom field for ${currentCustomField.display_name} found !!`
						);
					}

					const parentCustomFieldValue = form.getFieldValue(
						`custom_field_form_values.${parentCustomField.id}`
					);

					const allowedValues =
						currentCustomField.parent_field_info?.allowed_values?.[
							parentCustomFieldValue
						];

					return (
						<ZSelect
							data-testid={`child.${customField.id}`}
							disabled={isReadonly}
							getPopupContainer={trigger => trigger.parentNode}
							placeholder={`Please select ${currentCustomField.display_name}`}
							selectOptions={
								allowedValues?.map(allowedDropdownValue => ({
									value: allowedDropdownValue,
									name: allowedDropdownValue
								})) ?? []
							}
							mode={
								currentCustomField.field_type ===
								CustomFieldType.MultiSelect
									? "multiple"
									: undefined
							}
						/>
					);
				}

				return (
					<ZSelect
						data-testid={`dropdown.${customField.id}`}
						disabled={isReadonly}
						getPopupContainer={trigger => trigger.parentNode}
						placeholder={`Please select ${currentCustomField.display_name}`}
						onChange={parentValueChanged}
						selectOptions={
							currentCustomField.allowed_dropdown_values?.map(
								allowedDropdownValue => ({
									value: allowedDropdownValue,
									name: allowedDropdownValue
								})
							) ?? []
						}
						mode={
							currentCustomField.field_type ===
							CustomFieldType.MultiSelect
								? "multiple"
								: undefined
						}
					/>
				);
			}

			case CustomFieldType.DropdownFieldWithKeyValue: {
				return (
					<ZSelect
						placeholder={`Please select ${customField.display_name}`}
						disabled={isReadonly}
						selectOptions={
							customField.allowed_dropdown_key_values?.map(
								allowedDropdownValue => ({
									value: allowedDropdownValue.key,
									name: allowedDropdownValue.value
								})
							) ?? []
						}
					/>
				);
			}

			case CustomFieldType.CurrencyField: {
				return (
					<ZSelect
						placeholder={`Please select ${customField.display_name}`}
						disabled={isReadonly}
						selectOptions={CurrencyOptions.map(a => ({
							name: a.label,
							value: a.symbol
						}))}
					/>
				);
			}
			case CustomFieldType.FormulaField: {
				return (
					<ZInput
						data-testid="text-custom-field"
						disabled
						className="w-full"
					/>
				);
			}
			default: {
				return <Fragment />;
			}
		}
	};

	const isDateCustomField =
		customField.field_type === CustomFieldType.DateField;

	const initialValue = values
		? isDateCustomField && values[0]
			? moment(values[0])
			: customField.field_type === CustomFieldType.MultiSelect
			? values
			: values[0]
		: undefined;

	useEffect(() => {
		if (props.initialValues) {
			props.initialValues.forEach(value => {
				props.form.setFieldsValue({
					[value.id]: isDateCustomField
						? moment(value.value).toString() !== "Invalid date"
							? moment(value.value)
							: undefined
						: value.value
				});
			});
		}
	}, [props.initialValues]);

	return (
		<Form.Item
			labelCol={props.formAlignment?.labelCol}
			wrapperCol={props.formAlignment?.wrapperCol}
			label={
				props.shouldShowTooltip ? (
					<ZTooltip
						showtooltip
						title={capitalizeFirstLetter(customField.display_name)}
						placement="top"
					>
						<ZText className="truncate text-1">
							{capitalizeFirstLetter(customField.display_name)}
						</ZText>
					</ZTooltip>
				) : (
					`${capitalizeFirstLetter(customField.display_name)}`
				)
			}
			labelAlign="left"
			hasFeedback={true}
		>
			{form.getFieldDecorator(
				`custom_field_form_values.${customField.id}`,
				{
					rules: [
						{
							required:
								customField.mandatory_field_details?.for_ui ??
								false,
							message: `Please enter ${customField.display_name}`
						}
					],
					initialValue: initialValue
				}
			)(
				!(props.mergeTagUsed && data_type_merge_tag) ? (
					getCustomFieldFormItem(customField)
				) : (
					<ZEditorMergeTag
						disabled={isReadonly}
						id={`${props.from}-${customField.display_name}`}
						MergeTags={props?.mergeTags ?? []}
						placeholder={`Please enter ${customField.display_name}`}
						border={true}
						data_type={data_type_merge_tag}
					/>
				)
			)}
		</Form.Item>
	);
};

export default RenderCustomFieldFormItem;
